import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Plus, Search, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { PageHeader } from "@/components/page-header";
import { apiRequest } from "@/lib/queryClient";
import { Link, useLocation } from "wouter";
import { LenderFormModal } from "@/components/lenders/lender-form-modal";

// Lender type from schema (matches enum lender_type)
type LenderType = "bank" | "non_bank" | "private" | "smsf_lender";

// Lender status from schema (matches enum lender_status)
type LenderStatus = "active" | "inactive" | "pending_approval";

// Accreditation status from schema (matches enum accreditation_status)
type AccreditationStatus = "accredited" | "not_accredited" | "pending_accreditation" | "accreditation_expired";

interface Lender {
  id: string;
  lenderCode: string;
  lenderName: string;
  lenderType: LenderType;
  status: LenderStatus;
  accreditationStatus: AccreditationStatus | null;
  upfrontCommissionRate: string | null;
  trailCommissionRate: string | null;
  clawbackPeriodMonths: number | null;
  bdmName: string | null;
  bdmEmail: string | null;
  bdmPhone: string | null;
  createdAt: string;
  updatedAt: string;
}

export default function LenderList() {
  const [searchTerm, setSearchTerm] = useState("");
  const [lenderTypeFilter, setLenderTypeFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [addLenderModalOpen, setAddLenderModalOpen] = useState(false);
  const [, navigate] = useLocation();

  const { data, isLoading } = useQuery<Lender[]>({
    queryKey: ["/api/crm/lenders", searchTerm, lenderTypeFilter, statusFilter],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (searchTerm) params.append("search", searchTerm);
      if (lenderTypeFilter !== "all") params.append("lenderType", lenderTypeFilter);
      if (statusFilter !== "all") params.append("status", statusFilter);

      const response = await apiRequest("GET", `/api/crm/lenders?${params.toString()}`);
      return response.json();
    },
  });

  const lenders = Array.isArray(data) ? data : [];

  const lenderTypes = [
    { value: "all", label: "All Types" },
    { value: "bank", label: "Bank" },
    { value: "non_bank", label: "Non-Bank" },
    { value: "private", label: "Private" },
    { value: "smsf_lender", label: "SMSF Lender" },
  ];

  const statusOptions = [
    { value: "all", label: "All Status" },
    { value: "active", label: "Active" },
    { value: "inactive", label: "Inactive" },
    { value: "pending_approval", label: "Pending Approval" },
  ];

  const getStatusBadgeColor = (status: LenderStatus) => {
    switch (status) {
      case "active":
        return "bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200";
      case "inactive":
        return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200";
      case "pending_approval":
        return "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getAccreditationBadgeColor = (status: AccreditationStatus | null) => {
    if (!status) return "bg-gray-100 text-gray-800";
    switch (status) {
      case "accredited":
        return "bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200";
      case "not_accredited":
        return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200";
      case "pending_accreditation":
        return "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200";
      case "accreditation_expired":
        return "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const formatLenderType = (type: LenderType) => {
    const labels: Record<LenderType, string> = {
      bank: "Bank",
      non_bank: "Non-Bank",
      private: "Private",
      smsf_lender: "SMSF Lender",
    };
    return labels[type] || type;
  };

  const formatStatus = (status: LenderStatus) => {
    const labels: Record<LenderStatus, string> = {
      active: "Active",
      inactive: "Inactive",
      pending_approval: "Pending",
    };
    return labels[status] || status;
  };

  const formatAccreditationStatus = (status: AccreditationStatus | null) => {
    if (!status) return "-";
    const labels: Record<AccreditationStatus, string> = {
      accredited: "Accredited",
      not_accredited: "Not Accredited",
      pending_accreditation: "Pending",
      accreditation_expired: "Expired",
    };
    return labels[status] || status;
  };

  const formatCommissionRate = (rate: string | null) => {
    if (!rate) return "-";
    const numRate = parseFloat(rate);
    if (isNaN(numRate)) return "-";
    return `${(numRate * 100).toFixed(2)}%`;
  };

  return (
    <div className="flex h-screen w-full flex-col">
      <PageHeader
        title="Lenders"
        breadcrumbs={[{ label: "Home", href: "/" }, { label: "Lenders" }]}
        actions={
          <Button
            data-testid="button-add-lender"
            onClick={() => setAddLenderModalOpen(true)}
          >
            <Plus className="mr-2 h-4 w-4" />
            Add Lender
          </Button>
        }
      />

      <main className="flex-1 overflow-auto bg-muted/20 p-4">
        <div className="mx-auto max-w-7xl space-y-4">
          {/* Search and Filters */}
          <Card className="p-3">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search lenders..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 pr-9"
                  data-testid="input-search-lenders"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm("")}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    aria-label="Clear search"
                    data-testid="button-clear-search"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>

              <div className="flex gap-2">
                <Select value={lenderTypeFilter} onValueChange={setLenderTypeFilter}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Lender Type" />
                  </SelectTrigger>
                  <SelectContent>
                    {lenderTypes.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-[140px]">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    {statusOptions.map((status) => (
                      <SelectItem key={status.value} value={status.value}>
                        {status.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </Card>

          {/* Lenders Table */}
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Code</TableHead>
                  <TableHead>Lender Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>BDM Contact</TableHead>
                  <TableHead>Upfront Rate</TableHead>
                  <TableHead>Trail Rate</TableHead>
                  <TableHead>Accreditation</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-[100px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center">
                      Loading lenders...
                    </TableCell>
                  </TableRow>
                ) : lenders.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center">
                      No lenders found
                    </TableCell>
                  </TableRow>
                ) : (
                  lenders.map((lender) => (
                    <TableRow key={lender.id}>
                      <TableCell className="font-medium">{lender.lenderCode}</TableCell>
                      <TableCell>
                        <Link href={`/lenders/${lender.id}`} className="text-blue-600 hover:underline">
                          {lender.lenderName}
                        </Link>
                      </TableCell>
                      <TableCell>{formatLenderType(lender.lenderType)}</TableCell>
                      <TableCell>
                        {lender.bdmName ? (
                          <div className="text-sm">
                            <div className="font-medium">{lender.bdmName}</div>
                            {lender.bdmEmail && <div className="text-muted-foreground">{lender.bdmEmail}</div>}
                            {lender.bdmPhone && <div className="text-muted-foreground">{lender.bdmPhone}</div>}
                          </div>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell>{formatCommissionRate(lender.upfrontCommissionRate)}</TableCell>
                      <TableCell>{formatCommissionRate(lender.trailCommissionRate)}</TableCell>
                      <TableCell>
                        <Badge className={getAccreditationBadgeColor(lender.accreditationStatus)}>
                          {formatAccreditationStatus(lender.accreditationStatus)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={getStatusBadgeColor(lender.status)}>
                          {formatStatus(lender.status)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Link href={`/lenders/${lender.id}`} className="text-sm text-blue-600 hover:underline">
                          View
                        </Link>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Card>
        </div>
      </main>

      {/* Lender Form Modal */}
      <LenderFormModal
        open={addLenderModalOpen}
        onOpenChange={setAddLenderModalOpen}
        onLenderCreated={(lender) => {
          navigate(`/lenders/${lender.id}`);
        }}
      />
    </div>
  );
}
